import { useState } from 'react';
import { Play, Clock, CheckCircle, XCircle, Loader2, ChevronDown, ChevronUp, GitBranch } from 'lucide-react';
import { MermaidDiagram } from '@/components/ui/MermaidDiagram';
import { useWorkflowExecutions, useRunWorkflow, useDeals } from '@/lib/hooks';
import { cn, formatCurrency } from '@/lib/utils';
import {
  TransactionStructure,
  transactionStructureLabels,
  transactionStructureIcons,
  WorkflowStatus,
  type WorkflowStatusType,
} from '@/types/database';

interface Workflow {
  id: string;
  name: string;
  description: string;
  icon: string;
  category: 'core' | 'structure-specific' | 'analysis';
  applicableStructures: string[];
  estimatedTime: string;
  steps: string[];
  requiresDealId?: boolean;
  requiresMultipleDeals?: boolean;
  diagram?: string; // Mermaid diagram definition
}

const workflows: Workflow[] = [
  {
    id: 'deal-sourcing',
    name: 'Deal Sourcing Engine',
    description: 'Target 5-7 year old funds where LPs are skeptical but moat-building is complete and revenue inflection is imminent',
    icon: '🎯',
    category: 'core',
    applicableStructures: ['all'],
    estimatedTime: '2-4 hours',
    steps: [
      'Query SEC EDGAR Form D filings (2017-2021 vintages)',
      'Score "Sweet Spot" - bell curve peaking at 5-7 years',
      'Assess LP Skepticism indicators (fund age fatigue)',
      'Evaluate Moat Maturity (post-building, pre-revenue)',
      'Calculate Structural Discount (LP psychology vs fundamentals)',
      'Rank by Thesis Alignment (perfect/strong/moderate/weak)',
      'Generate targeted outreach for high-alignment opportunities',
    ],
    diagram: `flowchart TD
    A[📡 SEC EDGAR Query] --> B[Form D Filings 2017-2021]
    B --> C{Fund Age Check}
    C -->|5-7 Years| D[🎯 Sweet Spot Score: HIGH]
    C -->|<5 or >7 Years| E[Sweet Spot Score: LOW]
    D --> F[LP Skepticism Analysis]
    E --> F
    F --> G[Moat Maturity Assessment]
    G --> H[Structural Discount Calc]
    H --> I{Thesis Alignment}
    I -->|Perfect/Strong| J[✅ Priority Outreach]
    I -->|Moderate| K[📋 Secondary List]
    I -->|Weak| L[❌ Archive]

    style D fill:#10b981,color:#fff
    style J fill:#3b82f6,color:#fff
    style L fill:#ef4444,color:#fff`,
  },
  {
    id: 'portfolio-valuation',
    name: 'Portfolio Valuation',
    description: 'Calculate TRUE discount (headline - fee drag) to find real value vs LP psychology-driven discounts',
    icon: '📊',
    category: 'core',
    applicableStructures: ['all'],
    estimatedTime: '30-45 minutes',
    requiresDealId: true,
    steps: [
      'Extract NAV and fund financials',
      'Calculate remaining management fee burden',
      'Model carry waterfall impact',
      'Compute TRUE discount = Headline - Fee Drag',
      'Identify structural vs psychological discount component',
      'Flag opportunities where discount exceeds fee drag',
    ],
    diagram: `flowchart LR
    A[📄 Fund Data] --> B[NAV Analysis]
    B --> C[Headline Discount]
    B --> D[Fee Structure]
    D --> E[Mgmt Fee Drag]
    D --> F[Carry Impact]
    E --> G[Total Fee Drag]
    F --> G
    C --> H{TRUE Discount}
    G --> H
    H --> I[Headline - Fee Drag]
    I --> J{Discount Source?}
    J -->|Fee-Driven| K[⚠️ Fair Value]
    J -->|LP Psychology| L[✅ Opportunity]

    style L fill:#10b981,color:#fff
    style K fill:#f59e0b,color:#fff`,
  },
  {
    id: 'gp-consent-request',
    name: 'GP Consent Request',
    description: 'Request and track GP consent for secondary transfer (required before LP outreach)',
    icon: '📝',
    category: 'core',
    applicableStructures: [TransactionStructure.WHOLE_LP_INTEREST, TransactionStructure.PARTIAL_STRIP],
    estimatedTime: '15-30 days',
    requiresDealId: true,
    steps: [
      'Prepare consent request package',
      'Submit to GP administrator',
      'Track ROFR period timeline (typically 30 days)',
      'Monitor GP response',
      'Handle ROFR exercise or waiver',
      'Update consent status',
    ],
    diagram: `flowchart TD
    A[📝 Prepare Package] --> B[Submit to GP]
    B --> C[⏱️ ROFR Period Starts]
    C --> D{30 Days}
    D --> E{GP Response?}
    E -->|Consent Granted| F[✅ Proceed to Close]
    E -->|ROFR Exercised| G[🔄 GP Buys Position]
    E -->|No Response| H[⏳ Follow Up]
    H --> E
    G --> I[❌ Deal Terminated]
    F --> J[📋 Update Status]

    style F fill:#10b981,color:#fff
    style G fill:#f59e0b,color:#fff
    style I fill:#ef4444,color:#fff`,
  },
  {
    id: 'lp-acquisition',
    name: 'LP Acquisition Transaction',
    description: 'Execute full transfer with ROFR handling and GP consent',
    icon: '🤝',
    category: 'core',
    applicableStructures: [TransactionStructure.WHOLE_LP_INTEREST, TransactionStructure.PARTIAL_STRIP],
    estimatedTime: '30-45 days',
    requiresDealId: true,
    steps: [
      'Draft and send LOI with TRUE discount pricing',
      'Conduct due diligence on fund and GP',
      'Submit GP consent request',
      'Handle ROFR period (30 days)',
      'Prepare closing documents',
      'Execute transfer and fund capital call',
    ],
    diagram: `flowchart TD
    A[📋 LOI with TRUE Discount] --> B[🔍 Due Diligence]
    B --> C[Fund Analysis]
    B --> D[GP Track Record]
    C --> E[📝 GP Consent Request]
    D --> E
    E --> F{ROFR Period}
    F -->|Waived| G[Fast Track]
    F -->|30 Days| H[Wait Period]
    G --> I[📄 Closing Docs]
    H --> J{ROFR Decision}
    J -->|Not Exercised| I
    J -->|Exercised| K[❌ GP Takes Position]
    I --> L[💰 Execute Transfer]
    L --> M[✅ Position Acquired]

    style M fill:#10b981,color:#fff
    style K fill:#ef4444,color:#fff`,
  },
  {
    id: 'multi-fund-portfolio',
    name: 'Multi-Fund Portfolio Acquisition',
    description: 'Bundled acquisition of 3-5 fund interests from single seller - diversified thesis exposure',
    icon: '📦',
    category: 'structure-specific',
    applicableStructures: [TransactionStructure.MULTI_FUND_PORTFOLIO],
    estimatedTime: '45-60 days',
    requiresMultipleDeals: true,
    steps: [
      'Collect and prioritize fund list by thesis alignment',
      'Calculate blended TRUE discount across portfolio',
      'Develop pricing strategy (bulk discount)',
      'Submit parallel GP consents',
      'Prepare master documentation',
      'Execute coordinated close',
    ],
    diagram: `flowchart TD
    A[📦 Fund Portfolio] --> B[Fund 1]
    A --> C[Fund 2]
    A --> D[Fund 3+]
    B --> E[Thesis Score]
    C --> F[Thesis Score]
    D --> G[Thesis Score]
    E --> H[Blended TRUE Discount]
    F --> H
    G --> H
    H --> I[📝 Parallel GP Consents]
    I --> J{All Approved?}
    J -->|Yes| K[Master Close]
    J -->|Partial| L[Restructure Bundle]
    L --> I
    K --> M[✅ Portfolio Acquired]

    style M fill:#10b981,color:#fff`,
  },
  {
    id: 'partial-strip',
    name: 'Partial Strip Structuring',
    description: 'Structure 25-50% position purchase - lower capital, shared upside with skeptical LP',
    icon: '✂️',
    category: 'structure-specific',
    applicableStructures: [TransactionStructure.PARTIAL_STRIP],
    estimatedTime: '25-35 days',
    requiresDealId: true,
    steps: [
      'Determine optimal strip percentage (25-50%)',
      'Draft co-ownership agreement',
      'Negotiate GP consent for partial transfer',
      'Structure distribution split pro-rata',
      'Execute partial assignment',
    ],
    diagram: `flowchart LR
    A[💰 Full LP Position] --> B{Strip %}
    B -->|25-50%| C[Your Strip]
    B -->|50-75%| D[Seller Retains]
    C --> E[📄 Co-Ownership Agreement]
    D --> E
    E --> F[GP Consent]
    F --> G[Distribution Split]
    G --> H[Pro-Rata Distributions]
    H --> I[✅ Shared Upside]

    style C fill:#3b82f6,color:#fff
    style D fill:#6b7280,color:#fff
    style I fill:#10b981,color:#fff`,
  },
  {
    id: 'cv-monitor',
    name: 'CV Monitor',
    description: 'Track GP-led continuation vehicles - often signal moat maturity and revenue inflection',
    icon: '🔍',
    category: 'analysis',
    applicableStructures: ['all'],
    estimatedTime: 'Ongoing',
    steps: [
      'Monitor GP announcements for CV formation',
      'Analyze CV terms and stapled secondary pricing',
      'Evaluate roll vs. cash-out economics',
      'Identify LP liquidity during CV process',
      'Track participation deadlines',
    ],
    diagram: `flowchart TD
    A[📡 GP Announcement] --> B{CV Formation?}
    B -->|Yes| C[Analyze Terms]
    B -->|No| D[Continue Monitoring]
    C --> E[Stapled Secondary Price]
    C --> F[Roll Terms]
    E --> G{Better Than Market?}
    F --> G
    G -->|Yes| H[🎯 Target LPs Taking Cash]
    G -->|No| I[📋 Monitor Only]
    H --> J[Outreach During Election]
    D --> A

    style H fill:#10b981,color:#fff`,
  },
  {
    id: 'fee-negotiation',
    name: 'Fee Negotiation',
    description: 'Negotiate reduced fees to improve TRUE discount - key to capturing LP psychology discount',
    icon: '💰',
    category: 'analysis',
    applicableStructures: ['all'],
    estimatedTime: '1-2 weeks',
    requiresDealId: true,
    steps: [
      'Analyze current fee structure vs remaining term',
      'Calculate fee drag impact on TRUE discount',
      'Research GP precedents for fee reductions',
      'Draft negotiation points (mgmt fee, carry)',
      'Execute negotiation playbook',
      'Recalculate TRUE discount post-negotiation',
    ],
    diagram: `flowchart LR
    A[📊 Current Fees] --> B[Mgmt Fee Analysis]
    A --> C[Carry Structure]
    B --> D[Fee Drag Calc]
    C --> D
    D --> E[TRUE Discount Gap]
    E --> F{Negotiate?}
    F -->|High Impact| G[📝 GP Outreach]
    F -->|Low Impact| H[Proceed As-Is]
    G --> I[Fee Reduction]
    I --> J[Improved TRUE Discount]
    J --> K[✅ Better Returns]

    style K fill:#10b981,color:#fff
    style J fill:#3b82f6,color:#fff`,
  },
];

const statusConfig: Record<WorkflowStatusType, { icon: React.ReactNode; color: string; label: string }> = {
  [WorkflowStatus.PENDING]: { icon: <Clock className="h-4 w-4" />, color: 'text-text-tertiary', label: 'Pending' },
  [WorkflowStatus.RUNNING]: { icon: <Loader2 className="h-4 w-4 animate-spin" />, color: 'text-primary', label: 'Running' },
  [WorkflowStatus.COMPLETED]: { icon: <CheckCircle className="h-4 w-4" />, color: 'text-positive', label: 'Completed' },
  [WorkflowStatus.FAILED]: { icon: <XCircle className="h-4 w-4" />, color: 'text-negative', label: 'Failed' },
};

const categoryLabels = {
  core: 'Core Workflows',
  'structure-specific': 'Structure-Specific',
  analysis: 'Analysis & Monitoring',
};

export function WorkflowsPage(): React.ReactElement {
  const [expandedWorkflow, setExpandedWorkflow] = useState<string | null>(null);
  const [showRunModal, setShowRunModal] = useState(false);
  const [selectedWorkflow, setSelectedWorkflow] = useState<Workflow | null>(null);
  const [selectedDealId, setSelectedDealId] = useState<string>('');
  const [selectedDealIds, setSelectedDealIds] = useState<string[]>([]);
  const [stripPercentage, setStripPercentage] = useState<number>(35);

  const { data: executionsData, isLoading: executionsLoading } = useWorkflowExecutions();
  const { data: dealsData } = useDeals({ limit: 100 });
  const runWorkflow = useRunWorkflow();

  const executions = executionsData?.data ?? [];
  const deals = dealsData?.data ?? [];
  const activeDeals = deals.filter(d => d.stage !== 'closed' && d.stage !== 'archived');

  // Deal Sourcing configuration state
  const [dealSourcingConfig, setDealSourcingConfig] = useState({
    useSecEdgar: true,
    startDate: '2015-01-01',
    endDate: '2020-12-31',
    vintageYears: '2015,2016,2017,2018,2019,2020',
    minFundSize: '',
    maxFundSize: '',
    limit: '50',
  });

  const handleRunWorkflow = (workflow: Workflow): void => {
    setSelectedWorkflow(workflow);
    setShowRunModal(true);
  };

  const [workflowResult, setWorkflowResult] = useState<{
    success: boolean;
    message: string;
    details?: unknown;
  } | null>(null);

  const executeWorkflow = async () => {
    if (!selectedWorkflow) return;

    try {
      // Build parameters based on workflow type
      let parameters: Record<string, unknown> = {};

      // Deal Sourcing - SEC EDGAR configuration
      if (selectedWorkflow.id === 'deal-sourcing' && dealSourcingConfig.useSecEdgar) {
        parameters = {
          use_sec_edgar: true,
          start_date: dealSourcingConfig.startDate,
          end_date: dealSourcingConfig.endDate,
          vintage_years: dealSourcingConfig.vintageYears
            .split(',')
            .map(y => parseInt(y.trim()))
            .filter(y => !isNaN(y)),
          limit: parseInt(dealSourcingConfig.limit) || 50,
        };

        if (dealSourcingConfig.minFundSize) {
          parameters.min_fund_size = parseInt(dealSourcingConfig.minFundSize);
        }
        if (dealSourcingConfig.maxFundSize) {
          parameters.max_fund_size = parseInt(dealSourcingConfig.maxFundSize);
        }
      }

      // Workflows that require a deal_id
      if (selectedWorkflow.requiresDealId && selectedDealId) {
        parameters.deal_id = selectedDealId;
      }

      // Partial Strip - add strip percentage
      if (selectedWorkflow.id === 'partial-strip') {
        parameters.strip_percentage = stripPercentage / 100;
      }

      // Multi-Fund Portfolio - add deal_ids
      if (selectedWorkflow.id === 'multi-fund-portfolio' && selectedDealIds.length >= 2) {
        parameters.deal_ids = selectedDealIds;
      }

      // GP Consent Request - set action to 'request'
      if (selectedWorkflow.id === 'gp-consent-request') {
        parameters.action = 'request';
      }

      const input: { workflow_name: string; parameters: Record<string, unknown>; deal_id?: string } = {
        workflow_name: selectedWorkflow.id,
        parameters,
      };
      if (selectedDealId) {
        input.deal_id = selectedDealId;
      }
      const result = await runWorkflow.mutateAsync(input);

      // Parse the result to show meaningful info
      const resultData = result as Record<string, unknown>;
      let successMessage = `Workflow "${selectedWorkflow.name}" completed successfully!`;

      if (selectedWorkflow.id === 'deal-sourcing' && resultData.deals_imported) {
        successMessage = `Found ${resultData.filings_found || 0} SEC filings, identified ${resultData.candidates_identified || 0} candidates, imported ${resultData.deals_imported} deals worth $${((resultData.total_estimated_value as number || 0) / 1_000_000).toFixed(1)}M!`;
      } else if (selectedWorkflow.id === 'portfolio-valuation' && resultData.deals_valued !== undefined) {
        successMessage = `Valued ${resultData.deals_valued} deals. Hot: ${resultData.hot_deals || 0}, Warm: ${resultData.warm_deals || 0}, Cold: ${resultData.cold_deals || 0}`;
      } else if (selectedWorkflow.id === 'gp-consent-request' && resultData.consent_status) {
        successMessage = `GP consent request submitted! ROFR period: ${resultData.rofr_period_days || 30} days`;
      } else if (selectedWorkflow.id === 'lp-acquisition' && resultData.new_stage) {
        successMessage = `Deal advanced: ${resultData.previous_stage} → ${resultData.new_stage}`;
      } else if (selectedWorkflow.id === 'partial-strip' && resultData.strip_value) {
        successMessage = `Converted to ${stripPercentage}% partial strip: ${formatCurrency(resultData.strip_value as number)}`;
      } else if (selectedWorkflow.id === 'cv-monitor' && resultData.cv_candidates !== undefined) {
        successMessage = `Scanned ${resultData.deals_scanned || 0} deals. Found ${resultData.cv_candidates} CV candidates (${resultData.high_likelihood || 0} high, ${resultData.medium_likelihood || 0} medium)`;
      } else if (selectedWorkflow.id === 'fee-negotiation' && resultData.impact) {
        const impact = resultData.impact as { total_savings_over_term?: number; true_discount_improvement?: number };
        successMessage = `Fee analysis complete. Potential savings: ${formatCurrency(impact.total_savings_over_term || 0)}, TRUE discount improvement: ${((impact.true_discount_improvement || 0) * 100).toFixed(1)}%`;
      } else if (selectedWorkflow.id === 'multi-fund-portfolio' && resultData.portfolio_id) {
        successMessage = `Created portfolio with ${resultData.fund_count || 0} funds worth ${formatCurrency(resultData.total_nav as number || 0)}`;
      }

      setWorkflowResult({
        success: true,
        message: successMessage,
        details: result,
      });

      // Auto-close after showing success
      setTimeout(() => {
        setShowRunModal(false);
        setWorkflowResult(null);
        setSelectedDealId('');
        setSelectedDealIds([]);
      }, 5000);
    } catch (error) {
      setWorkflowResult({
        success: false,
        message: `Workflow failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
      });
    }
  };

  const groupedWorkflows = {
    core: workflows.filter(w => w.category === 'core'),
    'structure-specific': workflows.filter(w => w.category === 'structure-specific'),
    analysis: workflows.filter(w => w.category === 'analysis'),
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-text-primary">Workflows</h2>
        <p className="text-text-secondary">One-click workflow execution for deal management</p>
      </div>

      {/* Workflow Categories */}
      {Object.entries(groupedWorkflows).map(([category, categoryWorkflows]) => (
        <div key={category}>
          <h3 className="text-lg font-semibold text-text-primary mb-4">
            {categoryLabels[category as keyof typeof categoryLabels]}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {categoryWorkflows.map((workflow) => {
              const isExpanded = expandedWorkflow === workflow.id;

              return (
                <div
                  key={workflow.id}
                  className="bg-surface rounded-lg border border-border overflow-hidden hover:shadow-md transition-shadow"
                >
                  <div className="p-6">
                    <div className="flex items-start gap-4">
                      <span className="text-3xl">{workflow.icon}</span>
                      <div className="flex-1">
                        <h4 className="text-lg font-semibold text-text-primary">{workflow.name}</h4>
                        <p className="text-text-secondary mt-1 text-sm">{workflow.description}</p>

                        {/* Applicable Structures */}
                        <div className="flex items-center gap-2 mt-3">
                          {workflow.applicableStructures.includes('all') ? (
                            <span className="text-xs text-text-tertiary bg-muted px-2 py-1 rounded font-medium">
                              All structures
                            </span>
                          ) : (
                            workflow.applicableStructures.map((structure) => (
                              <span
                                key={structure}
                                className="text-xs text-text-secondary bg-muted px-2 py-1 rounded flex items-center gap-1 font-medium"
                              >
                                {transactionStructureIcons[structure as keyof typeof transactionStructureIcons]}
                                {transactionStructureLabels[structure as keyof typeof transactionStructureLabels]}
                              </span>
                            ))
                          )}
                        </div>

                        <div className="flex items-center justify-between mt-4">
                          <span className="text-xs text-text-tertiary font-medium">
                            Est. time: {workflow.estimatedTime}
                          </span>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => setExpandedWorkflow(isExpanded ? null : workflow.id)}
                              className="text-sm text-text-tertiary hover:text-text-primary flex items-center gap-1 font-medium"
                            >
                              {isExpanded ? 'Hide' : 'View'} steps
                              {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                            </button>
                            <button
                              onClick={() => handleRunWorkflow(workflow)}
                              className="px-4 py-2 bg-primary text-white text-sm rounded-md hover:bg-primary-hover transition-colors flex items-center gap-2 shadow-sm font-medium"
                            >
                              <Play className="h-4 w-4" />
                              Run
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Expanded Steps and Diagram */}
                  {isExpanded && (
                    <div className="px-6 pb-6 pt-0">
                      <div className="border-t border-border pt-4">
                        {/* Mermaid Diagram */}
                        {workflow.diagram && (
                          <div className="mb-4">
                            <div className="flex items-center gap-2 mb-2">
                              <GitBranch className="h-4 w-4 text-primary" />
                              <p className="text-sm font-semibold text-text-primary">Process Flow:</p>
                            </div>
                            <div className="bg-white rounded-lg border border-border overflow-hidden">
                              <MermaidDiagram
                                chart={workflow.diagram}
                                id={`workflow-${workflow.id}`}
                                className="p-2"
                              />
                            </div>
                          </div>
                        )}

                        {/* Steps List */}
                        <p className="text-sm font-semibold text-text-primary mb-2">Workflow Steps:</p>
                        <ol className="space-y-2">
                          {workflow.steps.map((step, index) => (
                            <li key={index} className="flex items-start gap-2 text-sm text-text-secondary">
                              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary text-white flex items-center justify-center text-xs font-semibold">
                                {index + 1}
                              </span>
                              {step}
                            </li>
                          ))}
                        </ol>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* Recent Executions */}
      <div className="bg-surface rounded-lg border border-border p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-text-primary mb-4">Recent Executions</h3>
        {executionsLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : executions.length === 0 ? (
          <p className="text-text-tertiary text-center py-8">No workflow executions yet</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-border">
              <thead className="bg-muted">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider">Workflow</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider">Started</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider">Triggered By</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border bg-surface">
                {executions.map((execution) => {
                  const status = statusConfig[execution.status];
                  const startTime = new Date(execution.started_at);

                  return (
                    <tr key={execution.id} className="hover:bg-surface-hover transition-colors">
                      <td className="px-4 py-3 text-sm font-semibold text-text-primary">
                        {execution.workflow_name}
                      </td>
                      <td className="px-4 py-3">
                        <div className={cn('flex items-center gap-2 text-sm font-medium', status.color)}>
                          {status.icon}
                          <span>{status.label}</span>
                        </div>
                        {execution.error_message && (
                          <p className="text-xs text-negative mt-1">{execution.error_message}</p>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-text-secondary font-mono">
                        {startTime.toLocaleDateString()} {startTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </td>
                      <td className="px-4 py-3 text-sm text-text-secondary">
                        {execution.triggered_by}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Run Workflow Modal */}
      {showRunModal && selectedWorkflow && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-modal">
          <div className="bg-surface rounded-lg p-6 max-w-md w-full mx-4 shadow-xl border border-border">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-3xl">{selectedWorkflow.icon}</span>
              <h3 className="text-lg font-semibold text-text-primary">{selectedWorkflow.name}</h3>
            </div>

            <p className="text-text-secondary mb-4">{selectedWorkflow.description}</p>

            <div className="bg-muted rounded-lg p-4 mb-4 border border-border">
              <p className="text-sm font-semibold text-text-primary mb-2">This workflow will:</p>
              <ul className="space-y-1">
                {selectedWorkflow.steps.slice(0, 3).map((step, index) => (
                  <li key={index} className="text-sm text-text-secondary flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                    {step}
                  </li>
                ))}
                {selectedWorkflow.steps.length > 3 && (
                  <li className="text-sm text-text-tertiary">
                    ...and {selectedWorkflow.steps.length - 3} more steps
                  </li>
                )}
              </ul>
            </div>

            <div className="flex items-center gap-2 text-sm text-text-tertiary mb-4 font-medium">
              <Clock className="h-4 w-4" />
              Estimated time: {selectedWorkflow.estimatedTime}
            </div>

            {/* Deal Sourcing Configuration */}
            {selectedWorkflow.id === 'deal-sourcing' && (
              <div className="bg-primary-subtle border border-primary rounded-lg p-4 mb-4">
                <h4 className="text-sm font-semibold text-primary mb-3">SEC EDGAR Configuration</h4>
                <p className="text-xs text-text-secondary mb-3">
                  Query SEC Form D filings to find older funds with LP liquidity opportunities.
                  Target: 5-7 year old funds past moat-building, with skeptical LPs but strong fundamentals.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-text-secondary mb-1">Start Date</label>
                    <input
                      type="date"
                      value={dealSourcingConfig.startDate}
                      onChange={(e) => setDealSourcingConfig({ ...dealSourcingConfig, startDate: e.target.value })}
                      className="w-full px-2 py-1 text-sm border border-border rounded bg-surface text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-text-secondary mb-1">End Date</label>
                    <input
                      type="date"
                      value={dealSourcingConfig.endDate}
                      onChange={(e) => setDealSourcingConfig({ ...dealSourcingConfig, endDate: e.target.value })}
                      className="w-full px-2 py-1 text-sm border border-border rounded bg-surface text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs font-medium text-text-secondary mb-1">Vintage Years (comma-separated)</label>
                    <input
                      type="text"
                      value={dealSourcingConfig.vintageYears}
                      onChange={(e) => setDealSourcingConfig({ ...dealSourcingConfig, vintageYears: e.target.value })}
                      className="w-full px-2 py-1 text-sm border border-border rounded bg-surface text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="2015,2016,2017,2018,2019,2020"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-text-secondary mb-1">Max Results</label>
                    <input
                      type="number"
                      value={dealSourcingConfig.limit}
                      onChange={(e) => setDealSourcingConfig({ ...dealSourcingConfig, limit: e.target.value })}
                      className="w-full px-2 py-1 text-sm border border-border rounded bg-surface text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
                      min="1"
                      max="100"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-text-secondary mb-1">Min Fund Size ($)</label>
                    <input
                      type="number"
                      value={dealSourcingConfig.minFundSize}
                      onChange={(e) => setDealSourcingConfig({ ...dealSourcingConfig, minFundSize: e.target.value })}
                      className="w-full px-2 py-1 text-sm border border-border rounded bg-surface text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="Optional"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Deal Selection for workflows that require a deal */}
            {selectedWorkflow.requiresDealId && (
              <div className="bg-muted border border-border rounded-lg p-4 mb-4">
                <h4 className="text-sm font-semibold text-text-primary mb-3">Select Deal</h4>
                {activeDeals.length === 0 ? (
                  <p className="text-sm text-text-tertiary">No active deals available. Run Deal Sourcing first.</p>
                ) : (
                  <select
                    value={selectedDealId}
                    onChange={(e) => setSelectedDealId(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-border rounded-md bg-surface text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="">
                      {selectedWorkflow.id === 'portfolio-valuation' ? '(All deals with headline discount)' : 'Choose a deal...'}
                    </option>
                    {activeDeals.map((deal) => (
                      <option key={deal.id} value={deal.id}>
                        {deal.name} - {formatCurrency(deal.target_value)} ({deal.stage})
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}

            {/* Partial Strip Configuration */}
            {selectedWorkflow.id === 'partial-strip' && selectedDealId && (
              <div className="bg-warning-subtle border border-warning rounded-lg p-4 mb-4">
                <h4 className="text-sm font-semibold text-warning mb-3">Strip Percentage</h4>
                <div className="flex items-center gap-4">
                  <input
                    type="range"
                    min="20"
                    max="60"
                    value={stripPercentage}
                    onChange={(e) => setStripPercentage(parseInt(e.target.value))}
                    className="flex-1"
                  />
                  <span className="text-lg font-bold text-warning w-16 text-center">{stripPercentage}%</span>
                </div>
                <p className="text-xs text-text-secondary mt-2">
                  Acquiring {stripPercentage}% of the LP position. Seller retains {100 - stripPercentage}%.
                </p>
              </div>
            )}

            {/* Multi-Fund Portfolio Deal Selection */}
            {selectedWorkflow.requiresMultipleDeals && (
              <div className="bg-primary-subtle border border-primary rounded-lg p-4 mb-4">
                <h4 className="text-sm font-semibold text-primary mb-3">Select Deals to Bundle (min 2)</h4>
                {activeDeals.length < 2 ? (
                  <p className="text-sm text-text-secondary">Need at least 2 active deals for portfolio bundling.</p>
                ) : (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {activeDeals.map((deal) => (
                      <label key={deal.id} className="flex items-center gap-2 p-2 bg-surface rounded border border-border cursor-pointer hover:bg-surface-hover transition-colors">
                        <input
                          type="checkbox"
                          checked={selectedDealIds.includes(deal.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedDealIds([...selectedDealIds, deal.id]);
                            } else {
                              setSelectedDealIds(selectedDealIds.filter(id => id !== deal.id));
                            }
                          }}
                          className="rounded text-primary"
                        />
                        <span className="text-sm text-text-primary font-medium">{deal.name}</span>
                        <span className="text-xs text-text-tertiary ml-auto font-mono">{formatCurrency(deal.target_value)}</span>
                      </label>
                    ))}
                  </div>
                )}
                {selectedDealIds.length > 0 && (
                  <p className="text-xs text-text-secondary mt-2 font-medium">
                    Selected: {selectedDealIds.length} deals
                  </p>
                )}
              </div>
            )}

            {/* Workflow Result Feedback */}
            {workflowResult && (
              <div className={cn(
                'mb-4 p-4 rounded-lg border',
                workflowResult.success
                  ? 'bg-positive-subtle border-positive text-positive'
                  : 'bg-negative-subtle border-negative text-negative'
              )}>
                <div className="flex items-center gap-2">
                  {workflowResult.success ? (
                    <CheckCircle className="h-5 w-5 text-positive" />
                  ) : (
                    <XCircle className="h-5 w-5 text-negative" />
                  )}
                  <p className="font-semibold">{workflowResult.message}</p>
                </div>
                {workflowResult.success && (
                  <p className="text-sm mt-1 text-text-secondary">
                    The workflow has been executed. Check Recent Executions for details.
                  </p>
                )}
              </div>
            )}

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowRunModal(false);
                  setWorkflowResult(null);
                }}
                className="px-4 py-2 border border-border text-text-secondary rounded-md hover:bg-surface-hover transition-colors"
                disabled={runWorkflow.isPending}
              >
                {workflowResult?.success ? 'Close' : 'Cancel'}
              </button>
              {!workflowResult?.success && (
                <button
                  onClick={executeWorkflow}
                  className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-hover flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm font-medium transition-colors"
                  disabled={
                    runWorkflow.isPending ||
                    (selectedWorkflow.requiresDealId && !selectedDealId && selectedWorkflow.id !== 'portfolio-valuation') ||
                    (selectedWorkflow.requiresMultipleDeals && selectedDealIds.length < 2)
                  }
                  title={
                    selectedWorkflow.requiresDealId && !selectedDealId && selectedWorkflow.id !== 'portfolio-valuation'
                      ? 'Please select a deal first'
                      : selectedWorkflow.requiresMultipleDeals && selectedDealIds.length < 2
                      ? 'Please select at least 2 deals'
                      : ''
                  }
                >
                  {runWorkflow.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Running...
                    </>
                  ) : (
                    <>
                      <Play className="h-4 w-4" />
                      Start Workflow
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
